import { Box, SxProps, Theme } from '@mui/material';
import React from 'react';
import { spacing } from '../Helper/ThemeHelper';

interface CheckmarkSpinnerProps {
  complete: boolean;
  failure: boolean;
  forwardedRef?: React.ForwardedRef<HTMLDivElement>;
}

class CheckmarkSpinnerComponent extends React.Component<CheckmarkSpinnerProps> {
  public render(): React.JSX.Element {
    const { complete, failure, forwardedRef } = this.props;

    const checkThickness = 6;
    const checkColor = (theme: Theme) => theme.palette.secondary.main;
    const loaderSize = (theme: Theme) => spacing(theme) * 12;
    const checkHeight = (theme: Theme) => loaderSize(theme) / 2.25;
    const checkWidth = (theme: Theme) => checkHeight(theme) / 2;
    const checkLeft = (theme: Theme) => {
      const size = loaderSize(theme);
      return size / 7 + size / 13;
    };

    const keyframes: SxProps<Theme> = {
      '@keyframes loaderSpin': {
        '0%': {
          transform: 'rotate(0deg)',
        },
        '100%': {
          transform: 'rotate(360deg)',
        },
      },
      '@keyframes checkmark': {
        '0%': {
          height: 0,
          width: 0,
          opacity: 1,
        },
        '20%': {
          height: 0,
          width: checkWidth,
          opacity: 1,
        },
        '40%': {
          height: checkHeight,
          width: checkWidth,
          opacity: 1,
        },
        '100%': {
          height: checkHeight,
          width: checkWidth,
          opacity: 1,
        },
      },
    };

    return (
      <Box
        component={'div'}
        sx={{
          margin: '0 auto 50px',
          border: checkThickness + 'px solid rgba(0, 0, 0, 0)',
          borderLeftColor: checkColor,
          animation: '$loaderSpin 1.2s infinite linear',
          position: 'relative',
          display: 'block',
          verticalAlign: 'top',
          borderRadius: '50%',
          width: loaderSize,
          height: loaderSize,
          ...(complete && {
            animation: 'none',
            borderColor: checkColor,
            transition: 'border 500ms ease-out',
          }),
        }}
        ref={forwardedRef}
      >
        {failure ? (
          <Box
            component={'div'}
            sx={{
              ...keyframes,
              '&::after': {
                opacity: 1,
                height: checkThickness,
                width: checkHeight,
                transformOrigin: 'center',
                content: "''",
                left: checkLeft,
                top: (theme) => checkHeight(theme) - checkThickness / 2,
                position: 'absolute',
                backgroundColor: checkColor,
                animationDuration: String(2000),
                animationTimingFunction: 'ease',
                animationName: '$checkmark',
                transform: 'rotate(-45deg)',
              },
              '&::before': {
                opacity: 1,
                height: checkThickness,
                width: checkHeight,
                transformOrigin: 'center',
                content: "''",
                left: checkLeft,
                top: (theme) => checkHeight(theme) - checkThickness / 2,
                position: 'absolute',
                backgroundColor: checkColor,
                animationDuration: String(2000),
                animationTimingFunction: 'ease',
                animationName: '$checkmark',
                transform: 'rotate(45deg)',
              },
              display: complete ? 'block' : 'none',
            }}
          />
        ) : (
          <Box
            component={'div'}
            sx={{
              '&::after': {
                opacity: 1,
                height: checkHeight,
                width: checkWidth,
                transformOrigin: 'left top',
                borderRight: checkThickness + 'px solid ' + checkColor,
                borderTop: checkThickness + 'px solid ' + checkColor,
                content: "''",
                left: checkLeft,
                top: (theme) => checkHeight(theme) + spacing(theme) / 2,
                position: 'absolute',
                animationDuration: String(2000),
                animationTimingFunction: 'ease',
                animationName: '$checkmark',
                transform: 'scaleX(-1) rotate(135deg)',
              },
              display: complete ? 'block' : 'none',
            }}
          />
        )}
      </Box>
    );
  }
}

// eslint-disable-next-line react/display-name
export const CheckmarkSpinner = React.forwardRef<HTMLDivElement, Omit<CheckmarkSpinnerProps, 'classes'>>(
  (props, ref) => <CheckmarkSpinnerComponent {...props} forwardedRef={ref} />,
);
